import { useEffect, useState, useRef } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/partnerProgramPage.module.scss';
import tableStyles from '../css/table.module.scss';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import CustomSearchInput from '../components/customSearchInput';
import CustomDropdown from '../components/customDropdown';
import { useClickAway } from 'react-use';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { PlanInterface } from '../helpers/objects/planObject';
import { formatPlanName } from '../helpers/functions';
import EsimForPlanChooser from '../components/esimForPlanChooser';

const GiftEsimsPage = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState<PlanInterface[]>([]);
  const [filteredPlans, setFilteredPlans] = useState<PlanInterface[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isActionOpen, setIsActionOpen] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const { t } = useTranslation();
  const [editingPlan, setEditingPlan] = useState<PlanInterface | null>(null);
  const [addingEsimOpen, setAddingEsimOpen] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(false);
  const [filterByCountry, setFilterByCountry] = useState<{ value: string; label: string }>({
    value: 'all',
    label: 'All Countries',
  });
  const [filterByCountryOpen, setFilterByCountryOpen] = useState<boolean>(false);
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);

  const filterByCountryDropRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const actionDropdownRef = useRef<HTMLDivElement>(null);

  const getBasePlans = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${EnvPaths.SERVER_URL}/skycop/get-local-product-list`);
      setPlans(response.data);
      setFilteredPlans(response.data);

      const uniqueCountries = Array.from(new Set(response.data.map((plan: PlanInterface) => plan.giftedEsim?.country.country_name))).map((country) => ({
        value: country as string,
        label: country as string,
      }));
      setCountries([{ value: 'all', label: 'All Countries' }, ...uniqueCountries]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActionClick = (id: string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  useClickAway(filterByCountryDropRef, () => {
    setFilterByCountryOpen(false);
  });

  useClickAway(actionDropdownRef, () => {
    setIsActionOpen(null);
  });

  const handleCountryChange = (item: { value: string; label: string }) => {
    setFilterByCountry(item);
    setFilterByCountryOpen(false);
  };

  const handleRemoveEsimFromPlan = async (plan: PlanInterface) => {
    try {
      await axios.post(
        `${EnvPaths.SERVER_URL}/admin/remove-esim-from-plan`,
        { planId: plan._id },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );
      setRefetch(!refetch);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    let filtered = [...plans];

    if (searchTerm) {
      filtered = filtered.filter((plan) => plan.giftedEsim?.name.toLowerCase().includes(searchTerm.toLowerCase()) || plan.giftedEsim?.country.country_name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    if (filterByCountry.value !== 'all') {
      filtered = filtered.filter((plan) => plan.giftedEsim?.country.country_name === filterByCountry.value);
    }

    setFilteredPlans(filtered);
  }, [searchTerm, filterByCountry, plans]);

  useEffect(() => {
    getBasePlans();
  }, [refetch]);

  return (
    <main className={styles.pageContainer}>
      <div className={globalPageStyle.pageFlexContainer} style={{ height: '100%' }}>
        {user && user.role === 'admin' ? (
          isLoading ? (
            <div className="loader" />
          ) : (
            <div style={{ overflow: 'auto' }}>
              <div className={styles.pageContent}>
                {/* Filters */}
                {/* <div className={styles.header}>
                  <div className={styles.headerLeft}>
                    <div className={styles.flexRow}>
                      <CustomSearchInput defaultValue={searchTerm} onSearch={setSearchTerm} icon="/assets/images/search-icon.svg" placeholder="Search by name or country..." />
                      <CustomDropdown ref={filterByCountryDropRef} isOpen={filterByCountryOpen} onToggle={() => setFilterByCountryOpen(!filterByCountryOpen)} options={countries} value={filterByCountry} onChange={handleCountryChange} width="255px" label="Filter by Country" />
                    </div>
                  </div>
                </div> */}

                {/* Table */}
                <div className={styles.tableBody}>
                  <div className={tableStyles.tableContainer}>
                    <table className={`${tableStyles.table}`}>
                      <thead className={tableStyles.tableHeader}>
                        <tr>
                          <th>Plan Name</th>
                          <th>eSIM Name</th>
                          <th>Country/Region</th>
                          <th>Data</th>
                          <th>Validity</th>
                          <th>Price</th>
                          <th>Plan Type</th>
                          <th className={tableStyles.actionColumn}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPlans.map((plan, index) => {
                          const esim = plan.giftedEsim;
                          return (
                            <tr key={plan._id}>
                              <td>{formatPlanName(t, plan.product.singleFlight, plan.product.family, plan.product.periodInMonths)}</td>
                              <td>{esim?.name ?? 'N/A'}</td>
                              <td>
                                {esim?.country?.country_flag && <span style={{ marginRight: '8px' }}>{esim?.country.country_flag}</span>}
                                {esim?.country?.country_name || 'N/A'}
                              </td>
                              <td>{esim?.data_GB ?? 0} GB</td>
                              <td>{esim?.validity_days_display || `${esim?.validity_days || 'N/A'} days`}</td>
                              <td>
                                {esim?.currency_obj?.symbol || esim?.currency}
                                {esim?.price ?? 'N/A'}
                              </td>
                              <td>{esim?.plan_type ?? 'N/A'}</td>
                              <td className={tableStyles.actionColumn}>
                                <div className={tableStyles.actionShape} ref={buttonRef}>
                                  <img
                                    src="/assets/images/dots.svg"
                                    alt="dots"
                                    onClick={(e) => {
                                      const buttonElement = e.currentTarget.parentElement as HTMLDivElement;
                                      handleActionClick(plan._id ?? index.toString(), buttonElement);
                                    }}
                                  />
                                  {isActionOpen === plan._id &&
                                    typeof window !== 'undefined' &&
                                    createPortal(
                                      <div
                                        ref={actionDropdownRef}
                                        className={`${tableStyles.actionShapeDropdown}`}
                                        style={{
                                          top: `${dropdownPosition.top}px`,
                                          left: `${dropdownPosition.left}px`,
                                        }}
                                      >
                                        <div
                                          className={tableStyles.actionShapeDropdownItem}
                                          onClick={() => {
                                            setEditingPlan(plan);
                                            setAddingEsimOpen(true);
                                          }}
                                        >
                                          Assign eSim
                                        </div>
                                        {esim?.package_type_id && (
                                          <div
                                            className={tableStyles.actionShapeDropdownItem}
                                            onClick={() => {
                                              handleRemoveEsimFromPlan(plan);
                                            }}
                                          >
                                            Remove eSim from plan
                                          </div>
                                        )}
                                      </div>,
                                      document.body
                                    )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {filteredPlans.length === 0 && <h2>No gift eSIM plans available</h2>}
              </div>
            </div>
          )
        ) : (
          <h1>You do not have permission to view this page</h1>
        )}
      </div>
      <EsimForPlanChooser
        plan={editingPlan}
        isOpen={addingEsimOpen}
        setRefetch={() => {
          setRefetch((prev) => !prev);
        }}
        onClose={() => setAddingEsimOpen(false)}
      />
    </main>
  );
};

export default GiftEsimsPage;
